/**
 * Returns the portion of `candidate` that extends beyond `typed`, or "" if
 * `typed` is not a (USPS-abbreviation-aware) prefix of `candidate`. Keeps
 * the candidate's casing for the visible tail so the ghost reads naturally
 * ("123 Main St" not "123 main st" when the user typed "123 main").
 *
 * The shadow is positioned against the user's typed text (ShadowText
 * renders Hidden(typed) + Ghost(suffix)), so the returned suffix is the
 * tail that should visually trail the user's keystrokes — not necessarily
 * a literal slice of the candidate. Example: typed "8602 West Grand",
 * candidate "8602 W Grand Ledge Hwy" → " Ledge Hwy". Smarty normalizes
 * directionals and street suffixes ("West" → "W", "Highway" → "Hwy") so
 * a pure literal-prefix match would hide the suffix whenever the user
 * spells those tokens out.
 */
export function deriveShadowSuffix(typed, candidate) {
  if (!candidate) return "";
  if (!typed) return candidate;
  if (
    candidate.length >= typed.length &&
    candidate.slice(0, typed.length).toLowerCase() === typed.toLowerCase()
  ) {
    return candidate.slice(typed.length);
  }
  const abbrSuffix = deriveAbbreviationAwareSuffix(typed, candidate);
  return abbrSuffix === null ? "" : abbrSuffix;
}

// USPS standard abbreviations encountered in Smarty autocomplete responses
// (directionals, street-type suffixes, secondary unit designators). Smarty is
// CASS-certified and standardizes to USPS Publication 28: directionals
// (Pub 28 §233), street suffixes (Appendix C1), and secondary unit designators
// (Appendix C2). Smarty's candidates carry the STANDARD abbreviation while the
// user typically types the full word, so this map keys the full word → its
// standard abbreviation; `tokensEquivalent` checks both directions.
//
// Scope: the authoritative USPS *primary* word → standard, for every entry
// where the two differ (entries that standardize to themselves — WAY, WALK,
// PASS, PATH, PIKE, RUN, MALL, ROW, LOOP, PARK, LAND, … — need no row;
// identical tokens already match). The many obscure column-2 variant
// spellings/abbreviations in Appendix C1 (AVN, AVENU, CNTER, STRVNUE, …) are
// intentionally omitted: Smarty never emits them, and some collide across
// singular/plural rows (e.g. MDW is the standard for MEADOW yet listed as a
// MEADOWS variant), so a flat long→short map can't carry them cleanly.
const STREET_ABBREVIATIONS = {
  // directionals (Pub 28 §233 — the eight standard directionals, complete)
  north: "n",
  south: "s",
  east: "e",
  west: "w",
  northeast: "ne",
  northwest: "nw",
  southeast: "se",
  southwest: "sw",
  // street-type suffixes (Appendix C1, primary word → standard)
  alley: "aly",
  anex: "anx",
  annex: "anx",
  arcade: "arc",
  avenue: "ave",
  bayou: "byu",
  beach: "bch",
  bend: "bnd",
  bluff: "blf",
  bluffs: "blfs",
  bottom: "btm",
  boulevard: "blvd",
  branch: "br",
  bridge: "brg",
  brook: "brk",
  brooks: "brks",
  burg: "bg",
  burgs: "bgs",
  bypass: "byp",
  camp: "cp",
  canyon: "cyn",
  cape: "cpe",
  causeway: "cswy",
  center: "ctr",
  centers: "ctrs",
  circle: "cir",
  circles: "cirs",
  cliff: "clf",
  cliffs: "clfs",
  club: "clb",
  common: "cmn",
  commons: "cmns",
  corner: "cor",
  corners: "cors",
  course: "crse",
  court: "ct",
  courts: "cts",
  cove: "cv",
  coves: "cvs",
  creek: "crk",
  crescent: "cres",
  crest: "crst",
  crossing: "xing",
  crossroad: "xrd",
  crossroads: "xrds",
  curve: "curv",
  dale: "dl",
  dam: "dm",
  divide: "dv",
  drive: "dr",
  drives: "drs",
  estate: "est",
  estates: "ests",
  expressway: "expy",
  extension: "ext",
  extensions: "exts",
  falls: "fls",
  ferry: "fry",
  field: "fld",
  fields: "flds",
  flat: "flt",
  flats: "flts",
  ford: "frd",
  fords: "frds",
  forest: "frst",
  forge: "frg",
  forges: "frgs",
  fork: "frk",
  forks: "frks",
  fort: "ft",
  freeway: "fwy",
  garden: "gdn",
  gardens: "gdns",
  gateway: "gtwy",
  glen: "gln",
  glens: "glns",
  green: "grn",
  greens: "grns",
  grove: "grv",
  groves: "grvs",
  harbor: "hbr",
  harbors: "hbrs",
  haven: "hvn",
  heights: "hts",
  highway: "hwy",
  hill: "hl",
  hills: "hls",
  hollow: "holw",
  island: "is",
  islands: "iss",
  junction: "jct",
  junctions: "jcts",
  key: "ky",
  keys: "kys",
  knoll: "knl",
  knolls: "knls",
  lake: "lk",
  lakes: "lks",
  landing: "lndg",
  lane: "ln",
  light: "lgt",
  lights: "lgts",
  loaf: "lf",
  lock: "lck",
  locks: "lcks",
  lodge: "ldg",
  manor: "mnr",
  manors: "mnrs",
  meadow: "mdw",
  meadows: "mdws",
  mill: "ml",
  mills: "mls",
  mission: "msn",
  motorway: "mtwy",
  mount: "mt",
  mountain: "mtn",
  mountains: "mtns",
  neck: "nck",
  orchard: "orch",
  overpass: "opas",
  parkway: "pkwy",
  parkways: "pkwy",
  passage: "psge",
  pine: "pne",
  pines: "pnes",
  place: "pl",
  plain: "pln",
  plains: "plns",
  plaza: "plz",
  point: "pt",
  points: "pts",
  port: "prt",
  ports: "prts",
  prairie: "pr",
  radial: "radl",
  ranch: "rnch",
  ranches: "rnch",
  rapid: "rpd",
  rapids: "rpds",
  rest: "rst",
  ridge: "rdg",
  ridges: "rdgs",
  river: "riv",
  road: "rd",
  roads: "rds",
  route: "rte",
  shoal: "shl",
  shoals: "shls",
  shore: "shr",
  shores: "shrs",
  skyway: "skwy",
  spring: "spg",
  springs: "spgs",
  square: "sq",
  squares: "sqs",
  station: "sta",
  stravenue: "stra",
  stream: "strm",
  street: "st",
  streets: "sts",
  summit: "smt",
  terrace: "ter",
  throughway: "trwy",
  trace: "trce",
  track: "trak",
  trafficway: "trfy",
  trail: "trl",
  tunnel: "tunl",
  turnpike: "tpke",
  underpass: "upas",
  union: "un",
  unions: "uns",
  valley: "vly",
  valleys: "vlys",
  viaduct: "via",
  view: "vw",
  views: "vws",
  village: "vlg",
  villages: "vlgs",
  ville: "vl",
  vista: "vis",
  well: "wl",
  wells: "wls",
  // secondary unit designators (Appendix C2, description → standard;
  // KEY/LOT/PIER/REAR/SIDE/SLIP/STOP/UNIT standardize to themselves)
  apartment: "apt",
  basement: "bsmt",
  building: "bldg",
  department: "dept",
  floor: "fl",
  front: "frnt",
  hangar: "hngr",
  lobby: "lbby",
  lower: "lowr",
  office: "ofc",
  penthouse: "ph",
  room: "rm",
  space: "spc",
  suite: "ste",
  trailer: "trlr",
  upper: "uppr",
};

// Grid house numbers in Queens, NY and Hawaii are written with a hyphen
// ("150-30", "73-4012") but Smarty (and the USPS delivery line) de-hyphenate
// them ("15030", "734012"). Residents type the hyphen — the USPS-preferred
// form — so the two spellings must be the SAME token, standardizing to the
// canonical exactly like an abbreviation (west→W). Scoped to digit-and-hyphen
// tokens (requiring at least one digit) so a hyphenated street name or surname
// ("Wilkes-Barre") is never merged with a spaced/de-hyphenated variant.
const GRID_NUMBER = /^[\d-]*\d[\d-]*$/;
function gridNumberEquivalent(a, b) {
  if (!GRID_NUMBER.test(a) || !GRID_NUMBER.test(b)) return false;
  return a.replace(/-/g, "") === b.replace(/-/g, "");
}

// Smarty never emits periods, but users type the punctuated abbreviation
// ("E.", "St.", "N.E."), so periods are input noise — strip every one before
// comparing. Trailing-only stripping would leave "n.e" for "N.E."; removing all
// periods normalizes both. Real street tokens don't contain periods, so this
// can't create a false match.
function normalizeToken(token) {
  return token.toLowerCase().replace(/\./g, "");
}

function tokensEquivalent(a, b) {
  const la = normalizeToken(a);
  const lb = normalizeToken(b);
  if (la === lb) return true;
  if (STREET_ABBREVIATIONS[la] === lb) return true;
  if (STREET_ABBREVIATIONS[lb] === la) return true;
  if (gridNumberEquivalent(la, lb)) return true;
  return false;
}

// Token-aligned match: walks typed tokens against candidate tokens with
// abbreviation tolerance. Returns the candidate's tail relative to the typed
// text — null when no alignment exists, vs "" when it aligns with nothing left
// to append; callers discriminate on === null (abbreviationAwarePrefixMatch).
//
//   typed     "8602 West Grand"
//   candidate "8602 W Grand Ledge Hwy"
//             →  " Ledge Hwy"
//
// The last typed token may be a literal prefix of its aligned candidate
// token (partial typing) — the in-progress completion is prepended to the
// remaining-tokens tail so the ghost reads as one continuous trailing
// string after the user's keystrokes.
function deriveAbbreviationAwareSuffix(typed, candidate) {
  const typedTokens = typed.split(/\s+/).filter(Boolean);
  const candidateTokens = candidate.split(/\s+/).filter(Boolean);
  if (!typedTokens.length) return candidate;
  if (typedTokens.length > candidateTokens.length) return null;

  const endsInSpace = /\s$/.test(typed);
  const completedCount = endsInSpace
    ? typedTokens.length
    : typedTokens.length - 1;

  for (let tokenIndex = 0; tokenIndex < completedCount; tokenIndex++) {
    if (!tokensEquivalent(typedTokens[tokenIndex], candidateTokens[tokenIndex]))
      return null;
  }

  if (endsInSpace) {
    const tail = candidateTokens.slice(completedCount);
    return tail.length ? tail.join(" ") : "";
  }

  const lastTyped = typedTokens[typedTokens.length - 1];
  const alignedCandidateToken = candidateTokens[completedCount];
  let partialCompletion;
  // The last typed token already satisfies its candidate token (e.g. "St" ≈ "Street"),
  // so offer no completion for it and ghost only the following words.
  if (tokensEquivalent(lastTyped, alignedCandidateToken)) {
    partialCompletion = "";
  } else if (
    alignedCandidateToken.toLowerCase().startsWith(lastTyped.toLowerCase())
  ) {
    partialCompletion = alignedCandidateToken.slice(lastTyped.length);
  } else {
    return null;
  }

  const remaining = candidateTokens.slice(completedCount + 1);
  if (!remaining.length) return partialCompletion;
  return `${partialCompletion} ${remaining.join(" ")}`;
}

export function abbreviationAwarePrefixMatch(typed, candidate) {
  if (!candidate) return false;
  if (!typed) return true;
  if (candidate.toLowerCase().startsWith(typed.toLowerCase())) return true;
  return deriveAbbreviationAwareSuffix(typed, candidate) !== null;
}

// Are two addr1 strings the same street, abbreviation-aware in BOTH directions
// (so "13103 Hitching Post Road" ≡ "…Rd")? The bare-building commit fires when
// the typed addr1 equals the candidate's bare street_line — neither a forward
// prefix (more to ghost) nor a longer string (an appended unit).
export function addr1Equivalent(a, b) {
  return (
    abbreviationAwarePrefixMatch(a, b) && abbreviationAwarePrefixMatch(b, a)
  );
}

// Would `candidate` complete the number the user is still typing into a LONGER
// number — "PO Box 1" → "PO Box 10001", "8602" → "86021 …"? A partial number is
// an ambiguous intent (box 1 is itself a valid, deliverable value; we can't know
// the user means 10001), so this is NOT a completion to push: callers suppress
// the street ghost and the street commit for it while keeping the candidate alive
// so its locality still ghosts. It fires only while the user is INSIDE the token
// (no trailing space); once they type past the number it is settled and normal
// completion resumes. An EQUAL number is not an extension — an exact-leaf
// "PO Box 1" still verifies. The candidate token must be all digits too, so an
// ordinal word completion ("1" → "1st") still ghosts.
export function extendsTrailingNumber(typed, candidate) {
  if (!typed || !candidate) return false;
  if (/\s$/.test(typed)) return false;
  const typedTokens = typed.trim().split(/\s+/);
  const candidateTokens = candidate.trim().split(/\s+/);
  const lastTyped = typedTokens[typedTokens.length - 1];
  const aligned = candidateTokens[typedTokens.length - 1];
  if (!aligned) return false;
  return (
    /^\d+$/.test(lastTyped) &&
    /^\d+$/.test(aligned) &&
    aligned.length > lastTyped.length &&
    aligned.startsWith(lastTyped)
  );
}

// Has the typed street extended PAST `streetLine` into the unit slot — i.e. is
// `streetLine` an abbreviation-aware prefix of `typed` AND does `typed` carry at
// least one further (unit) token beyond it? This is the unit-slot latch.
//
// It is ABBREVIATION-TOLERANT on purpose: on a hold surface the user's TYPED
// street is kept (e.g. "233 s wacker drive Ste ___") rather than rewritten to
// the canonical "233 S Wacker Dr". A leading-whitespace tail check
// (deriveShadowSuffix) only fired when the painted street equalled the
// canonical — with the typed spelling kept, "drive"≠"Dr" produced no
// whitespace-anchored tail and the latch silently dropped (the ghost snapped
// back). Token-walking with `tokensEquivalent` aligns "drive"≈"Dr", so the
// latch engages on the typed spelling too. The non-hold (canonical-painted)
// case still satisfies this — the canonical IS an abbreviation-aware match of
// itself — so the rewrite covers both surfaces.
//
// Tokens of `typed` are matched one-for-one against `streetLine`'s; every
// street token must align, and `typed` must contribute at least one non-empty
// token past the last street token (a partial designator like "S" or "Ste"
// counts — that's the user beginning the unit). A trailing space alone is NOT
// past (no unit token yet), matching the prior whitespace-tail behavior.
export function streetExtendsPastLine(typed, streetLine) {
  if (!streetLine) return false;
  const streetTokens = streetLine.split(/\s+/).filter(Boolean);
  const typedTokens = (typed || "").split(/\s+/).filter(Boolean);
  if (typedTokens.length <= streetTokens.length) return false;
  for (let i = 0; i < streetTokens.length; i += 1) {
    if (!tokensEquivalent(typedTokens[i], streetTokens[i])) return false;
  }
  return true;
}
